import React, {
	createContext,
	useCallback,
	useContext,
	useState,
	useMemo,
	type ReactNode,
} from 'react';
import { jiraHomeRoute } from '@atlassian/jira-router-routes-jira-home-routes/src/jiraHomeRoute.tsx';
import { oldJiraHomeRoute } from '@atlassian/jira-router-routes-jira-home-routes/src/oldJiraHomeRoute.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { usePathParam } from '@atlassian/react-resource-router';
import { storageProvider } from './utils.tsx';

const STORAGE_KEY = 'back-button-url';

type NavigateBackContextType = {
	saveCurrentRoute: (currentUrl: string) => void;
	previousUrl: string | null /* use this instead of goBack() */;
};

const NavigateBackContext = createContext<NavigateBackContextType | undefined>(undefined);

export const useNavigateBack = () => {
	const context = useContext(NavigateBackContext);
	if (!context) {
		throw new Error('useNavigateBack must be used within a NavigateBackProvider');
	}
	return context;
};

/**
 * Uses session storage instead of relying on browser history - to track of the last non-Settings menu page, to go "Back" to. See usages of saveCurrentRoute() - when new route is saved.
 */
export function NavigateBackProvider({ children }: { children: ReactNode }): React.JSX.Element {
	const [projectKey] = usePathParam('projectKey');
	const homeRouteToUse = fg('blu-7514_update_jira_home_your_work_to_for_you')
		? jiraHomeRoute
		: oldJiraHomeRoute;
	const defaultInitialUrl = projectKey ? `/projects/${projectKey}` : homeRouteToUse.path;
	const [previousUrl, setPreviousUrl] = useState<string>(
		() => storageProvider.get(STORAGE_KEY) ?? defaultInitialUrl,
	);

	const saveCurrentRoute = useCallback((currentUrl: string) => {
		setPreviousUrl(currentUrl);
		storageProvider.set(STORAGE_KEY, currentUrl);
	}, []);

	const value = useMemo(() => ({ saveCurrentRoute, previousUrl }), [saveCurrentRoute, previousUrl]);

	return <NavigateBackContext.Provider value={value}>{children}</NavigateBackContext.Provider>;
}
